import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { AppContext } from '../App';
import { MapPin, Phone, UserPlus } from 'lucide-react';
import { useBusinessContext } from '../hooks/useBusinessContext';
import { getCustomers, createCustomer, updateCustomer, getSales, getCustomerCredit, addSalePayment } from '../services/apiClient';
import PaymentModal from '../components/ui/PaymentModal';
import LoadingInline from '../components/ui/LoadingInline';

// Clients — source de vérité: /api/customers (voir backend/routes/customers.js).
// Le schéma serveur (CUSTOMERS: name/phone/email/address/notes) n'a pas de
// champ "prénom" séparé: les deux champs de saisie existants sont conservés
// pour ne pas changer l'UX, mais fusionnés en un seul "name" à l'envoi.
export default function CRMModule() {
  const { theme, user } = useContext(AppContext);
  const { businessId, loading: businessLoading } = useBusinessContext();

  const [clients, setClients] = useState([]);
  const [salesByCustomer, setSalesByCustomer] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [nom, setNom] = useState('');
  const [prenom, setPrenom] = useState('');
  const [tel, setTel] = useState('');
  const [adresse, setAdresse] = useState('');
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);

  // Mission 02 / Step 13: crédit du client actuellement ouvert (édition).
  const [credit, setCredit] = useState(null);
  const [creditLoading, setCreditLoading] = useState(false);
  const [payingSale, setPayingSale] = useState(null);

  const loadCredit = useCallback(async (customerId) => {
    if (!customerId) { setCredit(null); return; }
    setCreditLoading(true);
    try {
      const data = await getCustomerCredit(customerId);
      setCredit(data);
    } catch (err) {
      setCredit(null);
    } finally {
      setCreditLoading(false);
    }
  }, []);

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const load = async () => {
    if (!businessId) return;
    setLoading(true);
    setError('');
    try {
      const [customersRes, salesRes] = await Promise.all([
        getCustomers({ businessId, limit: 200 }),
        getSales({ businessId, limit: 200 })
      ]);
      setClients(customersRes.customers || []);

      // CA/nombre de ventes par client, calculé sur les ventes récentes
      // récupérées (facilement calculable côté client, pas de nouvel
      // endpoint dédié pour la V1 — voir étape 22).
      const stats = {};
      for (const sale of salesRes.sales || []) {
        if (!sale.customer_id || sale.status !== 'COMPLETED') continue;
        const entry = stats[sale.customer_id] || { count: 0, total: 0 };
        entry.count += 1;
        entry.total += Number(sale.total) || 0;
        stats[sale.customer_id] = entry;
      }
      setSalesByCustomer(stats);
    } catch (err) {
      setError(err?.message || 'Impossible de charger les clients.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [businessId]); // eslint-disable-line react-hooks/exhaustive-deps

  const resetForm = () => {
    setEditingId('');
    setNom(''); setPrenom(''); setTel(''); setAdresse('');
    setShowForm(false);
    setCredit(null);
  };

  const startEdit = (client) => {
    setEditingId(client.id);
    setNom(client.name || '');
    setPrenom('');
    setTel(client.phone || '');
    setAdresse(client.address || '');
    loadCredit(client.id);
    setShowForm(true);
  };

  const submitClient = async () => {
    const fullName = [prenom, nom].filter(Boolean).join(' ').trim();
    if (!fullName || !tel) return alert('Nom et téléphone obligatoires');
    setSaving(true);
    try {
      if (editingId) {
        await updateCustomer(editingId, { name: fullName, phone: tel, address: adresse });
      } else {
        await createCustomer({ businessId, name: fullName, phone: tel, address: adresse });
      }
      resetForm();
      await load();
    } catch (err) {
      alert(err?.message || 'Enregistrement du client impossible.');
    } finally {
      setSaving(false);
    }
  };

  const totalLabel = useMemo(() => `${clients.length} client${clients.length > 1 ? 's' : ''}`, [clients.length]);

  if (businessLoading || loading) {
    return <div className="page" style={{ padding: '20px' }}><LoadingInline text="Chargement des clients..." /></div>;
  }

  return (
    <div className="page" style={{ padding: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: isMobile ? 'stretch' : 'center', flexDirection: isMobile ? 'column' : 'row', gap: 12, marginBottom: 24 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 28, fontWeight: 800, color: theme.text }}>Clients</h2>
          <p style={{ margin: '6px 0 0', color: '#7A7568', fontSize: 14 }}>{totalLabel}</p>
        </div>
        <button
          onClick={() => (showForm ? resetForm() : setShowForm(true))}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: theme.primary, color: 'white', border: 'none', padding: '10px 16px', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer', width: isMobile ? '100%' : 'auto', justifyContent: 'center' }}
        >
          <UserPlus size={16} />
          {showForm ? 'Fermer' : 'Nouveau client'}
        </button>
      </div>

      {error && (
        <div style={{ background: '#fee2e2', color: '#991b1b', borderRadius: 10, padding: 12, marginBottom: 16 }}>{error}</div>
      )}

      {showForm && (
        <div className="card" style={{ marginBottom: 20, padding: 20, background: 'white', borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <h3 style={{ marginBottom: 15 }}>{editingId ? 'Modifier le client' : 'Ajouter un client à la base'}</h3>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
            <input placeholder="Nom" value={nom} onChange={e => setNom(e.target.value)} style={{ padding: 12, borderRadius: 8, border: '1px solid #ddd' }} />
            <input placeholder="Prénom" value={prenom} onChange={e => setPrenom(e.target.value)} style={{ padding: 12, borderRadius: 8, border: '1px solid #ddd' }} />
            <input placeholder="Téléphone" value={tel} onChange={e => setTel(e.target.value)} style={{ padding: 12, borderRadius: 8, border: '1px solid #ddd' }} />
            <input placeholder="Adresse" value={adresse} onChange={e => setAdresse(e.target.value)} style={{ padding: 12, borderRadius: 8, border: '1px solid #ddd' }} />
          </div>
          <div style={{ display: 'flex', gap: 10, marginTop: 15 }}>
            <button onClick={submitClient} disabled={saving} style={{ background: theme.primary, color: 'white', border: 'none', padding: '10px 20px', borderRadius: 8, cursor: saving ? 'not-allowed' : 'pointer', fontWeight: 'bold', opacity: saving ? 0.7 : 1 }}>
              {saving ? 'Enregistrement...' : editingId ? 'Mettre à jour' : 'Enregistrer le client'}
            </button>
          </div>
        </div>
      )}

      {/* Mission 02 / Step 13: crédit du client ouvert - "Combien me doit-il ?" */}
      {showForm && editingId && (
        <div className="card" style={{ marginBottom: 20, padding: 20, background: 'white', borderRadius: 12, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
          <h3 style={{ marginTop: 0, marginBottom: 15 }}>Crédit client</h3>
          {creditLoading ? (
            <p style={{ color: '#888' }}>Chargement...</p>
          ) : !credit || credit.totalOutstanding <= 0 ? (
            <p style={{ color: '#059669', fontWeight: 600 }}>Aucune dette en cours.</p>
          ) : (
            <>
              <div style={{ marginBottom: 16 }}>
                <span style={{ fontSize: 13, color: '#888', display: 'block' }}>TOTAL À RECEVOIR</span>
                <strong style={{ fontSize: 26, color: '#b91c1c' }}>{credit.totalOutstanding.toLocaleString()} {user?.devise?.symbole || 'FCFA'}</strong>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {credit.sales.map((s) => (
                  <div key={s.saleId} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 10, padding: 12, background: '#fef2f2', borderRadius: 10 }}>
                    <div>
                      <strong>{s.saleNumber}</strong>
                      <span style={{ marginLeft: 8, color: '#888', fontSize: 13 }}>{new Date(s.date).toLocaleDateString('fr-FR')}</span>
                      {s.shop && <span style={{ marginLeft: 8, color: '#888', fontSize: 13 }}>· {s.shop}</span>}
                      <p style={{ margin: '4px 0 0', fontSize: 13, color: '#666' }}>
                        Total {s.total.toLocaleString()} — Déjà payé {s.paid.toLocaleString()} — Reste <strong style={{ color: '#b91c1c' }}>{s.remaining.toLocaleString()}</strong>
                      </p>
                    </div>
                    <button
                      onClick={() => setPayingSale(s)}
                      style={{ background: theme.primary, color: 'white', border: 'none', padding: '8px 14px', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: 13 }}
                    >
                      Enregistrer un paiement
                    </button>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {payingSale && (
        <PaymentModal
          sale={{ saleNumber: payingSale.saleNumber, customerName: nom, remaining: payingSale.remaining }}
          onClose={() => { setPayingSale(null); loadCredit(editingId); }}
          onSubmit={(payload) => addSalePayment(payingSale.saleId, payload)}
        />
      )}

      {clients.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 48, border: '2px dashed #e2e8f0', borderRadius: 16, background: 'white' }}>
          <UserPlus size={30} color="#9FB8A0" aria-hidden="true" style={{ marginBottom: 10 }} />
          <h3 style={{ margin: 0, fontSize: 17, color: theme.text }}>Aucun client pour le moment</h3>
          <p style={{ color: '#7A7568', margin: '8px 0 16px', fontSize: 14 }}>
            Vos clients apparaîtront ici, ou ajoutez-en un manuellement.
          </p>
          <button
            onClick={() => setShowForm(true)}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: theme.primary, color: 'white', border: 'none', padding: '10px 16px', borderRadius: 10, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}
          >
            <UserPlus size={16} /> Nouveau client
          </button>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 15 }}>
          {clients.map(c => {
            const stats = salesByCustomer[c.id] || { count: 0, total: 0 };
            return (
              <div key={c.id} className="card" style={{ padding: 16, background: 'white', borderRadius: 12, border: '1px solid #eee', cursor: 'pointer' }} onClick={() => startEdit(c)}>
                <div style={{ fontSize: 18, fontWeight: 'bold', marginBottom: 5 }}>{c.name}</div>
                <p style={{ fontSize: 14, color: '#666', marginBottom: 10, display: 'grid', gap: 6 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><Phone size={14} /> {c.phone || 'N/A'}</span>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}><MapPin size={14} /> {c.address || 'Aucune adresse'}</span>
                </p>

                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingTop: 10, borderTop: '1px solid #f5f5f5' }}>
                  <span style={{ fontSize: 12, color: '#888' }}>
                    {stats.count} vente{stats.count > 1 ? 's' : ''}
                  </span>
                  <strong style={{ color: theme.primary }}>
                    {stats.total.toLocaleString()} {user?.devise?.symbole || 'FCFA'}
                  </strong>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
